import React, { useState, useEffect, useCallback } from 'react';

const BRAND = '#667eea';
const BRAND2 = '#764ba2';
const BG = '#0a0a0a';
const SURFACE = 'rgba(255,255,255,0.05)';
const BORDER = 'rgba(255,255,255,0.1)';

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ─── Login Screen ────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/admin/emails', {
        headers: { 'x-admin-password': password },
      });
      if (res.status === 401) {
        setError('Incorrect password.');
      } else {
        const emails = await res.json();
        sessionStorage.setItem('admin_pw', password);
        onLogin(password, emails);
      }
    } catch {
      setError('Could not connect. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh', background: BG, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      <div style={{
        width: 360, background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${BORDER}`, borderRadius: 16,
        padding: '40px 36px',
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          backgroundClip: 'text', fontSize: 22, fontWeight: 700,
          letterSpacing: '-0.03em', marginBottom: 8,
        }}>
          PhotoVoice
        </div>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, marginBottom: 32, marginTop: 0 }}>
          Admin Inbox
        </p>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 500, marginBottom: 8, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            style={{
              width: '100%', padding: '10px 14px', boxSizing: 'border-box',
              background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`,
              borderRadius: 8, color: '#fff', fontSize: 15, outline: 'none',
              marginBottom: 12,
            }}
          />
          {error && (
            <p style={{ color: '#f87171', fontSize: 13, marginBottom: 12, marginTop: 0 }}>{error}</p>
          )}
          <button
            type="submit"
            disabled={loading || !password}
            style={{
              width: '100%', padding: '11px 0',
              background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
              border: 'none', borderRadius: 8, color: '#fff',
              fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading || !password ? 0.6 : 1,
            }}
          >
            {loading ? 'Checking…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Email List Item ─────────────────────────────────────────────────────────
function EmailItem({ email, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', padding: '14px 16px',
        background: selected
          ? `linear-gradient(135deg, rgba(102,126,234,0.2), rgba(118,75,162,0.15))`
          : 'transparent',
        border: 'none',
        borderBottom: `1px solid ${BORDER}`,
        borderLeft: selected ? `3px solid ${BRAND}` : '3px solid transparent',
        cursor: 'pointer', color: '#fff',
        transition: 'background 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{
          fontSize: 13, fontWeight: email.read ? 400 : 700,
          color: email.read ? 'rgba(255,255,255,0.7)' : '#fff',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          maxWidth: 160,
        }}>
          {email.fromName || email.fromAddress}
        </span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', flexShrink: 0, marginLeft: 8 }}>
          {formatDate(email.receivedAt).split(',')[0]}
        </span>
      </div>
      <div style={{
        fontSize: 12, fontWeight: email.read ? 400 : 600,
        color: email.read ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.85)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {email.subject}
      </div>
      {!email.read && (
        <div style={{
          display: 'inline-block', marginTop: 6,
          width: 7, height: 7, borderRadius: '50%',
          background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
        }} />
      )}
    </button>
  );
}

// ─── Email Detail ─────────────────────────────────────────────────────────────
function EmailDetail({ email, adminPw, onReplySent }) {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState('');
  const [error, setError] = useState('');

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    setError('');
    setSentMsg('');
    try {
      const res = await fetch('/api/admin/reply', {
        method: 'POST',
        headers: { 'x-admin-password': adminPw, 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailId: email.id, replyBody: replyText }),
      });
      if (res.ok) {
        setSentMsg('Reply sent.');
        setReplyText('');
        onReplySent({ body: replyText, sentAt: new Date().toISOString() });
      } else {
        setError('Failed to send reply.');
      }
    } catch {
      setError('Failed to send reply.');
    }
    setSending(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Email header */}
      <div style={{ padding: '24px 28px 20px', borderBottom: `1px solid ${BORDER}` }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
          {email.subject}
        </h2>
        <div style={{ display: 'grid', gap: 4 }}>
          {[
            ['From', `${email.fromName ? `${email.fromName} ` : ''}<${email.fromAddress}>`],
            ['Date', formatDate(email.receivedAt)],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
              <span style={{ color: 'rgba(255,255,255,0.4)', minWidth: 40 }}>{label}</span>
              <span style={{ color: 'rgba(255,255,255,0.8)' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Email body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
        {email.text ? (
          <pre style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontSize: 14, lineHeight: 1.7,
            color: 'rgba(255,255,255,0.8)',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
          }}>
            {email.text}
          </pre>
        ) : email.html ? (
          <iframe
            srcDoc={email.html}
            sandbox="allow-same-origin"
            title="email-body"
            style={{
              width: '100%', minHeight: 320, border: 'none',
              borderRadius: 8, background: '#fff',
            }}
            onLoad={e => {
              const doc = e.target.contentDocument;
              if (doc) e.target.style.height = doc.body.scrollHeight + 32 + 'px';
            }}
          />
        ) : (
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>(no body)</p>
        )}

        {/* Previous replies */}
        {email.replies?.length > 0 && (
          <div style={{ marginTop: 28, borderTop: `1px solid ${BORDER}`, paddingTop: 20 }}>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              Your replies
            </p>
            {email.replies.map((r, i) => (
              <div key={i} style={{
                background: `linear-gradient(135deg, rgba(102,126,234,0.1), rgba(118,75,162,0.08))`,
                border: `1px solid rgba(102,126,234,0.2)`,
                borderRadius: 10, padding: '14px 16px', marginBottom: 12,
              }}>
                <pre style={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: 13, lineHeight: 1.6,
                  color: 'rgba(255,255,255,0.75)', whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word', margin: '0 0 8px',
                }}>
                  {r.body}
                </pre>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                  Sent {formatDate(r.sentAt)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reply box */}
      <div style={{ padding: '16px 28px 24px', borderTop: `1px solid ${BORDER}` }}>
        <textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder={`Reply to ${email.fromAddress}…`}
          rows={4}
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.06)',
            border: `1px solid ${BORDER}`,
            borderRadius: 10, padding: '12px 14px',
            color: '#fff', fontSize: 14, lineHeight: 1.6,
            fontFamily: "'Inter', system-ui, sans-serif",
            resize: 'vertical', outline: 'none',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
          <button
            onClick={sendReply}
            disabled={sending || !replyText.trim()}
            style={{
              padding: '9px 22px',
              background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
              border: 'none', borderRadius: 8, color: '#fff',
              fontSize: 14, fontWeight: 600,
              cursor: sending || !replyText.trim() ? 'not-allowed' : 'pointer',
              opacity: sending || !replyText.trim() ? 0.5 : 1,
              boxShadow: `0 4px 14px rgba(102,126,234,0.3)`,
            }}
          >
            {sending ? 'Sending…' : 'Send Reply'}
          </button>
          {sentMsg && <span style={{ color: '#4ade80', fontSize: 13 }}>{sentMsg}</span>}
          {error && <span style={{ color: '#f87171', fontSize: 13 }}>{error}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Compose Modal ────────────────────────────────────────────────────────────
function ComposeModal({ adminPw, onClose }) {
  const [to, setTo] = useState('');
  const [from, setFrom] = useState('support');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      setError('To, Subject and Message are all required.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await fetch('/api/admin/compose', {
        method: 'POST',
        headers: { 'x-admin-password': adminPw, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: to.trim(), from, subject: subject.trim(), body: body.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        onClose();
      } else {
        setError(data.error ?? 'Failed to send.');
      }
    } catch {
      setError('Failed to send.');
    }
    setSending(false);
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.07)', border: `1px solid ${BORDER}`,
    borderRadius: 8, padding: '10px 14px', color: '#fff',
    fontSize: 14, fontFamily: "'Inter', system-ui, sans-serif", outline: 'none',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(0,0,0,0.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: 560, background: '#111', border: `1px solid ${BORDER}`,
        borderRadius: 16, padding: '28px 32px',
        fontFamily: "'Inter', system-ui, sans-serif",
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#fff' }}>New Email</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* From */}
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>From</label>
            <select
              value={from}
              onChange={e => setFrom(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="support">support@photovoice.app</option>
              <option value="inbox">inbox@photovoice.app</option>
            </select>
          </div>

          {/* To */}
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>To</label>
            <input
              type="email"
              value={to}
              onChange={e => setTo(e.target.value)}
              placeholder="recipient@example.com"
              style={inputStyle}
            />
          </div>

          {/* Subject */}
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Subject"
              style={inputStyle}
            />
          </div>

          {/* Body */}
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Message</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Write your message…"
              rows={7}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
            />
          </div>
        </div>

        {error && <p style={{ color: '#f87171', fontSize: 13, marginTop: 10, marginBottom: 0 }}>{error}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              padding: '9px 20px', background: 'transparent',
              border: `1px solid ${BORDER}`, borderRadius: 8,
              color: 'rgba(255,255,255,0.5)', fontSize: 14, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={sending}
            style={{
              padding: '9px 24px',
              background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
              border: 'none', borderRadius: 8, color: '#fff',
              fontSize: 14, fontWeight: 600,
              cursor: sending ? 'not-allowed' : 'pointer',
              opacity: sending ? 0.6 : 1,
              boxShadow: `0 4px 14px rgba(102,126,234,0.3)`,
            }}
          >
            {sending ? 'Sending…' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sent Item ───────────────────────────────────────────────────────────────
function SentItem({ email, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', padding: '14px 16px',
        background: selected
          ? `linear-gradient(135deg, rgba(102,126,234,0.2), rgba(118,75,162,0.15))`
          : 'transparent',
        border: 'none',
        borderBottom: `1px solid ${BORDER}`,
        borderLeft: selected ? `3px solid ${BRAND}` : '3px solid transparent',
        cursor: 'pointer', color: '#fff',
        transition: 'background 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{
          fontSize: 13, fontWeight: 400, color: 'rgba(255,255,255,0.7)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160,
        }}>
          To: {email.to}
        </span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', flexShrink: 0, marginLeft: 8 }}>
          {formatDate(email.sentAt).split(',')[0]}
        </span>
      </div>
      <div style={{
        fontSize: 12, fontWeight: 400, color: 'rgba(255,255,255,0.5)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {email.subject}
      </div>
    </button>
  );
}

// ─── Sent Detail ──────────────────────────────────────────────────────────────
function SentDetail({ email }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '24px 28px 20px', borderBottom: `1px solid ${BORDER}` }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
          {email.subject}
        </h2>
        <div style={{ display: 'grid', gap: 4 }}>
          {[
            ['To', email.to],
            ['From', email.from],
            ['Sent', formatDate(email.sentAt)],
            ...(email.replyToId ? [['Type', 'Reply to incoming email']] : [['Type', 'New email']]),
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
              <span style={{ color: 'rgba(255,255,255,0.4)', minWidth: 40 }}>{label}</span>
              <span style={{ color: 'rgba(255,255,255,0.8)' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
        <pre style={{
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: 14, lineHeight: 1.7,
          color: 'rgba(255,255,255,0.8)',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
        }}>
          {email.body}
        </pre>
      </div>
    </div>
  );
}

// ─── Main Inbox ───────────────────────────────────────────────────────────────
export default function AdminInbox() {
  const [adminPw, setAdminPw] = useState(() => sessionStorage.getItem('admin_pw') || '');
  const [tab, setTab] = useState('inbox'); // 'inbox' | 'sent'
  const [emails, setEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [composing, setComposing] = useState(false);

  const loadEmails = useCallback(async (pw = adminPw) => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/emails', { headers: { 'x-admin-password': pw } });
      if (res.ok) setEmails(await res.json());
    } finally {
      setLoading(false);
    }
  }, [adminPw]);

  const loadSentEmails = useCallback(async (pw = adminPw) => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/sent', { headers: { 'x-admin-password': pw } });
      if (res.ok) setSentEmails(await res.json());
    } finally {
      setLoading(false);
    }
  }, [adminPw]);

  useEffect(() => { if (adminPw) loadEmails(); }, [adminPw, loadEmails]);

  const handleLogin = (pw, initialEmails) => {
    setAdminPw(pw);
    setEmails(initialEmails);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem('admin_pw');
    setAdminPw('');
    setEmails([]);
    setSentEmails([]);
    setSelected(null);
  };

  const handleSelectEmail = async (id) => {
    const res = await fetch(`/api/admin/emails?id=${id}`, { headers: { 'x-admin-password': adminPw } });
    if (res.ok) {
      const email = await res.json();
      setSelected(email);
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, read: true } : e)));
    }
  };

  const handleReplySent = (reply) => {
    setSelected((prev) => ({
      ...prev,
      replies: [...(prev.replies || []), reply],
    }));
  };

  const handleSwitchTab = (newTab) => {
    setTab(newTab);
    setSelected(null);
    if (newTab === 'sent') loadSentEmails();
    else loadEmails();
  };

  if (!adminPw) return <LoginScreen onLogin={handleLogin} />;

  const unreadCount = emails.filter((e) => !e.read).length;

  const tabStyle = (active) => ({
    border: 'none', cursor: 'pointer',
    padding: '4px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
    color: active ? '#fff' : 'rgba(255,255,255,0.4)',
    background: active ? 'rgba(255,255,255,0.1)' : 'transparent',
    transition: 'background 0.15s, color 0.15s',
  });

  return (
    <div style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: BG, fontFamily: "'Inter', system-ui, sans-serif", color: '#fff',
    }}>
      {/* Top bar */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', height: 56,
        borderBottom: `1px solid ${BORDER}`,
        background: 'rgba(255,255,255,0.02)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text', fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em',
          }}>
            PhotoVoice
          </span>
          {/* Inbox / Sent tabs */}
          <div style={{ display: 'flex', gap: 2, marginLeft: 8 }}>
            <button onClick={() => handleSwitchTab('inbox')} style={tabStyle(tab === 'inbox')}>
              Inbox
              {unreadCount > 0 && (
                <span style={{
                  marginLeft: 6,
                  background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
                  color: '#fff', fontSize: 10, fontWeight: 700,
                  borderRadius: 99, padding: '1px 6px',
                }}>
                  {unreadCount}
                </span>
              )}
            </button>
            <button onClick={() => handleSwitchTab('sent')} style={tabStyle(tab === 'sent')}>
              Sent
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => setComposing(true)}
            style={{
              background: `linear-gradient(135deg, ${BRAND}, ${BRAND2})`,
              border: 'none', borderRadius: 7, padding: '5px 12px',
              color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              boxShadow: `0 2px 8px rgba(102,126,234,0.35)`,
            }}
          >
            + Compose
          </button>
          <button
            onClick={() => tab === 'sent' ? loadSentEmails() : loadEmails()}
            title="Refresh"
            style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 7, padding: '5px 12px',
              color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer',
            }}
          >
            ↻ Refresh
          </button>
          <button
            onClick={handleSignOut}
            style={{
              background: 'transparent', border: `1px solid ${BORDER}`,
              borderRadius: 7, padding: '5px 12px',
              color: 'rgba(255,255,255,0.5)', fontSize: 13, cursor: 'pointer',
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Sidebar */}
        <aside style={{
          width: 280, flexShrink: 0, overflowY: 'auto',
          borderRight: `1px solid ${BORDER}`,
        }}>
          {loading && (
            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13, padding: 24 }}>
              Loading…
            </p>
          )}
          {tab === 'inbox' && !loading && emails.length === 0 && (
            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13, padding: 24 }}>
              No emails yet.
            </p>
          )}
          {tab === 'sent' && !loading && sentEmails.length === 0 && (
            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13, padding: 24 }}>
              No sent emails yet.
            </p>
          )}
          {tab === 'inbox' && emails.map((email) => (
            <EmailItem
              key={email.id}
              email={email}
              selected={selected?.id === email.id}
              onClick={() => handleSelectEmail(email.id)}
            />
          ))}
          {tab === 'sent' && sentEmails.map((email) => (
            <SentItem
              key={email.id}
              email={email}
              selected={selected?.id === email.id}
              onClick={() => setSelected(email)}
            />
          ))}
        </aside>

        {/* Detail pane */}
        <main style={{ flex: 1, overflow: 'hidden' }}>
          {selected && tab === 'inbox' && (
            <EmailDetail
              email={selected}
              adminPw={adminPw}
              onReplySent={handleReplySent}
            />
          )}
          {selected && tab === 'sent' && (
            <SentDetail email={selected} />
          )}
          {!selected && (
            <div style={{
              height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.2)', fontSize: 14,
            }}>
              {tab === 'inbox' ? 'Select an email to read' : 'Select a sent email to view'}
            </div>
          )}
        </main>
      </div>

      {composing && (
        <ComposeModal adminPw={adminPw} onClose={() => setComposing(false)} />
      )}
    </div>
  );
}
